'use strict';

import * as util from '../util/Common.js';
import {EventManager} from '../util/EventManager.js';
import {Point2D} from '../util/Geometry.js';
import {Bounds, BoxDisposition} from '../content/BoxContent.js'
import * as domUtil from '../util/Dom.js';
import {UiComponent} from '../ui/UiComponent.js';
import {RelativeContainer} from '../ui/RelativeContainer.js';
import {ImageViewControl} from '../viewer/ImageViewControl.js';
import {IIIFCanvasResource} from '../content/IIIFCanvasContent.js';

const privateMap = util.createPrivateMapper();
function strokeLine(ctx, pts) {
  if (pts.length < 2) {
    return;
  }
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  const l = pts.length;
  for (let i = 1; i < l; i ++){
    ctx.lineTo(pts[i].x, pts[i].y);
  }
  ctx.stroke();
};

function clip(ctx, clipPoints){
  if (clipPoints.length > 1) {
    // with clipping
    ctx.beginPath();
    ctx.moveTo(clipPoints[0].x, clipPoints[0].y);
    const l = clipPoints.length;
    for (let i = 1; i < l; i++) {
      ctx.lineTo(clipPoints[i].x, clipPoints[i].y);
    }
    ctx.closePath();
    ctx.clip();
  }
};

function setColorStops(grad, colorStops){
  const l = colorStops.length;
  for (let i = 0; i < l; i++) {
    const cs = colorStops[i];
    grad.addColorStop(cs[0],cs[1]);
  }
};
function fillShadow(ctx, from, to, shadowLen, shadowAngle, rectAngle, colorStops){
  let grad
  grad = ctx.createLinearGradient(
    from.x, from.y,
    from.x + Math.cos(shadowAngle) * shadowLen, from.y + Math.sin(shadowAngle) * shadowLen
  );
  setColorStops(grad, colorStops);

  const delta = (new Point2D(shadowLen, 0)).rotate(rectAngle);
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(from.x + delta.x, from.y + delta.y);
  ctx.lineTo(to.x + delta.x, to.y + delta.y);
  ctx.lineTo(to.x, to.y);
  ctx.closePath();
  ctx.fill();
};
function radialShadow(ctx, center, shadowLen, ang1, ang2, colorStops){
  let grad
  grad = ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, shadowLen);
  setColorStops(grad, colorStops);
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.moveTo(center.x, center.y);
  ctx.arc(center.x, center.y, shadowLen, ang1, ang2);
  ctx.closePath();
  ctx.fill();
};


function resetSequenceViewScene(pv){
  if (pv.resourceLoadObj) {
    pv.resourceLoadObj.abort();
    pv.resourceLoadObj = false;
  }
  if (pv.normalizeAnimation) {
    pv.normalizeAnimation.stop()
    pv.normalizeAnimation = null
  }
  if (pv.loadingIndicator) {
    pv.loadingIndicator.close();
  }
  pv.loadingScene = null;
  pv.closeDomLayer();
  pv.closeMessageLayer();
  util.each(pv.currentContentViewComponents, (component) => {
    component.unlink();
  });
  pv.currentContentViewComponents.length = 0;
  util.each(pv.sceneComponents, (component) => {
    component.unlink();
  });
}

function showError(pv, msg, ev) {
  if (ev) {
    console.error(ev);
  }
  if (pv.loadingIndicator) {
    pv.loadingIndicator.close();
  }
  pv.openMessageLayer(pv.createErrorMessage(msg));
}
function loadPage(pv, viewNo, scene, sceneParam) {
  const canvasInfo = scene.source
  const items = []
  const canvasResource = new IIIFCanvasResource(canvasInfo, items)
  if (sceneParam) {
    canvasResource.size = sceneParam.size
  }
  const queue = [];
  if (canvasInfo.items.length == 0) {
    return;
  }
  util.each(canvasInfo.items, (image) => {
    queue.push(image);
  });
  function next(){
    if (queue.length == 0) {
      return;
    }
    const v = queue.shift();
    const loader = pv.resourceLoaders[v.type];
    if (!loader){
      if (params.failure) {
        showError(pv, 'loader not found. type:' + v.type);
      }
      return;
    }
    loader.load(v.source, function(resource){
      items.push(resource);
      // if (pv.currentViewNo !== viewNo) {
      //   return
      // }
      if (v.xywh) {
        resource.bounds.position.set(v.xywh.x - (canvasInfo.width / 2) + (v.xywh.width / 2), v.xywh.y - (canvasInfo.height / 2) + (v.xywh.height / 2))
        resource.bounds.size.set(v.xywh.width, v.xywh.height)
      }
      if (pv.contentView) {
        pv.contentView.requestRender()
      }
      for (let comp of pv.currentContentViewComponents) {
        if (comp.requestRender) {
          comp.requestRender()
        }
      }
      
      next();
    }, {
      //TODO failure
    });
  }
  next();
  return {
    resource: canvasResource
  }
}
function changeSequenceViewScene(pv) {
  const viewNo = pv.controller.viewNo
  const view = pv.controller.getView(viewNo)
  const contentView = pv.contentView;
  if (!contentView) {
    return
  }

  let prevZoomRatio = -1
  if (contentView.control && contentView.control.box) {
    const zoomRange = contentView.control.box.getZoomRange()
    if (zoomRange[0] != zoomRange[1]) {
      prevZoomRatio = (contentView.control.box.disposition.zoom - zoomRange[0]) / (zoomRange[1] - zoomRange[0])
      prevZoomRatio = Math.min(1, Math.max(0, prevZoomRatio))
    }
  }


  const scenes = []
  pv.currentViewNo = viewNo

  const resourceOption = {
    items: []
  }
  const annoQueue = [];
  const currentScenes = {}

  if (pv.handOverViewItems) {
    // hand over view items
    resourceOption.items = []
    for (let item of pv.handOverViewItems) {
      resourceOption.items.push(item)
      if (item) {
        scenes.push(item.scene)
        currentScenes[item.scene.id] = true
      }
    }
    pv.handOverViewItems = null
  } else {
    for (let itemIdx = 0; itemIdx < view.sceneIdxs.length; itemIdx++) {
      const pageScheneNo = view.sceneIdxs[itemIdx]
      const sceneParam = view.sceneParams[itemIdx]
      const scene = pv.controller.getScene(pageScheneNo);
      scenes.push(scene);
      if (!scene) {
        resourceOption.items.push(null)
        continue
      }
      currentScenes[scene.id] = true
      const page = loadPage(pv, viewNo, scene, sceneParam)
      resourceOption.items.push({
        scene: scene,
        contentType: pv.contentTypes[page.resource.type],
        resource: page.resource
      })
    }
  }
  for (let itemIdx = 0; itemIdx < resourceOption.items.length; itemIdx++) {
    const page = resourceOption.items[itemIdx]
    if (!page) {
      continue
    }
    const scene = page.scene
    const annoOffset = new Point2D()
    if (view.sceneIdxs.length == 2) {
      annoOffset.y = (view.size.y - scene.source.height) / 2
      const innerDelta = (page.resource.size.x - scene.source.width) / 2
      if (itemIdx == 1) {
        annoOffset.x = view.size.x / 2 + innerDelta + (pv.turnCenterSep / 2) 
      } else {
        annoOffset.x = (view.size.x / 2 - page.resource.size.x) + innerDelta - (pv.turnCenterSep / 2) 
      }
    }
    if (page.resource.annotationLists) {
      util.each(page.resource.annotationLists, (annoList) => {
        annoQueue.push({
          scene: scene,
          offset: annoOffset,
          annoList: annoList
        });
      })
    }
  }

  let prev
  const prevView = pv.controller.getView(viewNo - 1)
  if (prevView) {
    prev = {
      viewNo: viewNo - 1,
      items: []
    }
    for (let itemIdx = 0; itemIdx < prevView.sceneIdxs.length; itemIdx++) {
      const pageScheneNo = prevView.sceneIdxs[itemIdx]
      const sceneParam = prevView.sceneParams[itemIdx]
      const scene = pv.controller.getScene(pageScheneNo);
      if (!scene) {
        prev.items.push(null)
        continue
      }
      const page = loadPage(pv, viewNo - 1, scene, sceneParam)
      prev.items.push({
        scene: scene,
        contentType: pv.contentTypes[page.resource.type],
        resource: page.resource
      })
    }
  }
  let next
  const nextView = pv.controller.getView(viewNo + 1)
  if (nextView) {
    next = {
      viewNo: viewNo + 1,
      items: []
    }
    for (let itemIdx = 0; itemIdx < nextView.sceneIdxs.length; itemIdx++) {
      const pageScheneNo = nextView.sceneIdxs[itemIdx]
      const sceneParam = nextView.sceneParams[itemIdx]
      const scene = pv.controller.getScene(pageScheneNo);
      if (!scene) {
        next.items.push(null)
        continue
      }
      const page = loadPage(pv, viewNo + 1, scene, sceneParam)
      next.items.push({
        scene: scene,
        contentType: pv.contentTypes[page.resource.type],
        resource: page.resource
      })
    }
  }
  if (pv.controller.reverseOrder) {
    resourceOption.left = next
    resourceOption.right = prev
  } else {
    resourceOption.left = prev
    resourceOption.right = next
  }
  resourceOption.size = view.size
  resourceOption.centerSep = pv.turnCenterSep
  // resourceOption.boundaryBoxSize = pv.maxSize

  util.each(pv.sceneComponents, (component) => {
    component.link(scenes);
  });

  const baseContentType = pv.contentTypes['image']
  const components = baseContentType.components
  const iiifCanvasRenderer = pv.contentTypes['iiifcanvas'].renderer
  const contentType = {
    initialize: function(view){
      view.control.box.fit();
    },
    control: 'image',
    components: components,
    annotationListRenderer: baseContentType.annotationListRenderer,
    renderer: iiifCanvasRenderer
  }
  util.each(contentType.components, (name) => {
    const component = pv.contentViewComponents[name];
    if (!component) {
      return;
    }
    component.link(contentView);
    pv.currentContentViewComponents.push(component);
  });
  let renderer
  let resource
  renderer = pagedRenderer
  resource = new PagedImageResource(resourceOption)

  const contentControl = pv.contentControls[contentType.control];

  contentView.annotationDisabled = false
  let itemSessions
  let turnMotion
  if (pv.animation) {
    pv.animation.stop()
    pv.animation = null
  }
  if (pv.handOverContentViewSession && pv.handOverContentViewSession.itemSessions) {
    itemSessions = {...pv.handOverContentViewSession.itemSessions}
    if (pv.handOverContentViewSession.turnMotion) {
      if (pv.handOverAnimation) {
        turnMotion = pv.handOverContentViewSession.turnMotion
        pv.animation = pv.handOverAnimation
      }
    }
  }

  contentView.setView(renderer, contentControl, resource);
  if (itemSessions) {
    contentView.session.itemSessions = {}
    for (let i = 0; i < resource.allItems.length; i++) {
      const item = resource.allItems[i]
      if (!item) {
        continue
      }
      contentView.session.itemSessions[item.scene.id] = itemSessions[item.scene.id]
    }
  }
  if (turnMotion) {
    contentView.annotationDisabled = true
    contentView.session.turnMotion = turnMotion
    pv.animation.animate()
    contentView.requestRender()
  }
  pv.handOverContentViewSession = null
  pv.handOverAnimation = null

  if (!pv.session.initialized) {
    contentControl.box.fit()
    pv.session.initialized = true
  }
  contentView.annotationListRenderer = null;
  if (contentType.annotationListRenderer && annoQueue.length) {
    contentView.annotationListRenderer = contentType.annotationListRenderer;
    const annoListResources = [];
    function next(){
      if (annoQueue.length == 0) {
        contentView.annotationListResources = annoListResources;
        return;
      }
      const anno = annoQueue.shift();
      const source = anno.annoList;
      pv.resourceLoadObj = pv.annotationListResourceLoader.loadAnnotationList(source, function(annoResource){
        if (!currentScenes[anno.scene.id]) {
          annoQueue.length = 0;
          return;
        }
        annoResource.size = resource.size;
        annoResource.offset = anno.offset
        annoListResources.push(annoResource);
        next();
      }, {
        failure: (ev) => {
          console.error(ev);
        },
        progress: (ev) => {
          // Do nothing for now
        },
        always: () => {
          pv.resourceLoadObj = false;
        }
      });
    }
    next();
  }

  if (pv.normalizeAnimation) {
    pv.normalizeAnimation.stop()
    pv.normalizeAnimation = null
  }
  const baseOfs = new Point2D(contentView.control.box.disposition.offset)
  // const normOfs = contentView.control.box.normalizeOffset(baseOfs)
  const normOfs = new Point2D()
  const baseZoom = contentView.control.box.disposition.zoom
  const normZoomRange = contentView.control.box.getZoomRange()
  // let normZoom = Math.max(normZoomRange[0], Math.min(normZoomRange[1], baseZoom))
  // if (prevZoomRatio >= 0) {
  //   if (normZoomRange[0] != normZoomRange[1]) {
  //     normZoom = normZoomRange[0] + (normZoomRange[1] - normZoomRange[0]) * prevZoomRatio
  //   }
  // }
  let normZoom = contentView.control.box.calcFitZoom()
  if (turnMotion) {
    if (!normOfs.equals(baseOfs) || normZoom != baseZoom) {
      // normalized offset move animation
      contentView.control.box.disposition.offset.set(normOfs)
      const delta = normOfs.sub(baseOfs)
      const deltaZoom = normZoom - baseZoom
      const start = (new Date()).getTime()
      const dur = 300
      let animationTimeout
      pv.normalizeAnimation = {
        stop: function(){
          pv.normalizeAnimation = null
          if (animationTimeout) {
            cancelAnimationFrame(animationTimeout)
          }
          contentView.requestRender();
          contentView.control.box.disposition.offset.set(normOfs)
          contentView.control.box.disposition.zoom = normZoom
        },
        animate: function(){
          animationTimeout = false
          const now = (new Date()).getTime()
          const dt = now - start
          if (dt > dur) {
            this.stop()
            return
          }
          let t = dt / dur
          let dr =  1 - (--t) * t// * t * t
          const dpt = delta.mul(dr)
          const pt = baseOfs.add(dpt)
          contentView.control.box.disposition.offset.set(pt)
          contentView.control.box.disposition.zoom = baseZoom + dr * deltaZoom

          contentView.requestRender();
          animationTimeout = requestAnimationFrame(() => {
            this.animate()
          })
        }
      }
      pv.normalizeAnimation.animate()
    }
  } else {
    contentView.control.box.fit()
    // contentView.control.box.disposition.offset.set(normOfs)
    // contentView.control.box.disposition.zoom = normZoom
  }
  contentView.requestRender();
}

function initializeItems(pv, items) {
  for (let item of items) {
    if (!item) {
      continue
    }
    item.control = {
      box: {
        disposition: new BoxDisposition()
      }
    }
    item.renderPosition = new Point2D()
    pv.allItems.push(item)
  }
  if (items.length == 2) {
    const halfCenterSep = pv.centerSep / 2
    if (items[0]) {
      items[0].renderPosition.x = - items[0].resource.size.x / 2 - halfCenterSep
    }
    if (items[1]) {
      items[1].renderPosition.x = items[1].resource.size.x / 2 + halfCenterSep
    }
  }
}

export class PagedImageResource {
  constructor(option){
    const pv = privateMap(this);
    pv.centerSep = option.centerSep
    pv.size = new Point2D(option.size);
    pv.allItems = []
    this.boundaryBoxSize = option.boundaryBoxSize
    this.left = option.left
    this.right = option.right
    this.items = option.items

    initializeItems(pv, option.items)
    if (this.left) {
      initializeItems(pv, this.left.items)
    }
    if (this.right) {
      initializeItems(pv, this.right.items)
    }
    this.leftEdge = 0
    this.rightEdge = pv.size.x
    if (this.items.length == 2) {
      const mid = pv.size.x / 2
      const halfCenterSep = pv.centerSep / 2
      if (this.items[0]) {
        this.leftEdge = mid - this.items[0].resource.size.x - halfCenterSep
      }
      if (this.items[1]) {
        this.rightEdge = mid + this.items[1].resource.size.x + halfCenterSep
      }
    }
    pv.bounds = new Bounds();
    pv.bounds.position.set(0, 0);
    pv.bounds.size.copy(pv.size);
  }
  get bounds(){
    return privateMap(this).bounds;
  }

  get type(){
    return 'image';
  }

  get size(){
    return privateMap(this).size.clone();
  }

  get allItems(){
    return privateMap(this).allItems;
  }
}
function renderItems(view, disposition, session, items){
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (!item) {
      continue
    }
    const itemDisp = item.control.box.disposition
    const zoom = disposition.zoom

    itemDisp.zoom = zoom
    itemDisp.angle = disposition.angle
    itemDisp.offset.x = disposition.offset.x + (item.renderPosition.x * zoom)
    itemDisp.offset.y = disposition.offset.y + (item.renderPosition.y * zoom)

    item.contentType.renderer.render(view, item.resource, item.control, session.itemSessions[item.scene.id])
  }
}
const pagedRenderer = {
  render: function(view, resource, control, session) {
    if (!view || !resource || !control) {
      return;
    }

    const ctx = view.context2d;
    const disposition = control.box.disposition
    if (!session.itemSessions) {
      session.itemSessions = {}
    }
    for (let i = 0; i < resource.allItems.length; i++) {
      const item = resource.allItems[i]
      if (!item) {
        continue
      }
      if (!session.itemSessions[item.scene.id]) {
        session.itemSessions[item.scene.id] = {}
      }
    }
    const turnMotion = session.turnMotion
    const z = disposition.zoom
    const a = disposition.angle
    const isTurn = turnMotion && turnMotion.turning

    if (isTurn) {
      // turn motion rendering
      const sideFactor = turnMotion.sideFactor
      const progress = turnMotion.progress
      const symProgress = 1 - Math.abs(0.5 - progress) * 2
      let backOpacity
      let foreOpacity
      let underOpacity
      let clipBackground = false
      if (turnMotion.fromView.sceneIdxs.length == 2) {
        if (turnMotion.progressReversed) {
          backOpacity = Math.min(1, (1 - progress) * 8)
          foreOpacity = 1//Math.min(1, (1- progress) * 8)
          underOpacity = backOpacity
          if (turnMotion.toView.sceneIdxs.length != 2) {
            clipBackground = true
            backOpacity = Math.min(1, (1 - progress) * 8)
            foreOpacity = Math.min(1, progress * 8)
            underOpacity = backOpacity
          } else {
            underOpacity = 1
          }
        } else {
          backOpacity = Math.min(1, progress * 8)
          foreOpacity = Math.min(1, (1 - progress) * 8)
          if (turnMotion.toView.sceneIdxs.length != 2) {
            backOpacity = Math.min(1, progress * 8)
            foreOpacity = Math.min(1, (1 - progress) * 8)
            underOpacity = foreOpacity
          } else {
            underOpacity = 1
          }
        }
      } else {
        backOpacity = Math.min(1, progress * 8)
        foreOpacity = Math.min(1, (1 - progress) * 8)
        underOpacity = foreOpacity
      }
      // backOpacity = 1
      // foreOpacity = 1
      // underOpacity = 1

      const vcp = view.center;
      const vsz = view.size;
      const convPx = (function(){
        const dpt = vcp.add(disposition.offset)
        return function(pt){
          const px = dpt.add(pt.mul(z));
          px.x = Math.round(px.x)
          px.y = Math.round(px.y)
          return px
        };
      })();
      const da = vcp.angle();
      const dg = vcp.distance();
      const dt = new Point2D(
          Math.max(Math.abs(Math.cos(da - a)), Math.abs(Math.cos(-da - a))),
          Math.max(Math.abs(Math.sin(da - a)), Math.abs(Math.sin(-da - a)))
        ).mul(dg).sub(vcp);
      dt.x = Math.round(dt.x)
      dt.y = Math.round(dt.y)

      let clipPx;
      let backClipPx;
      if (sideFactor < 0) {
        clipPx = [new Point2D(vsz.x + dt.x, -dt.y)];
        backClipPx = [new Point2D(-dt.x, -dt.y)];
      } else {
        clipPx = [new Point2D(-dt.x, -dt.y)];
        backClipPx = [new Point2D(vsz.x + dt.x, -dt.y)];
      }

      const clipAngle = turnMotion.clipAngle
      const upPx = convPx(turnMotion.upFoldPoint);
      const lowPx = convPx(turnMotion.lowFoldPoint);
      let upCenterPx
      let lowCenterPx
      let midCenterPx

      if (turnMotion.upCenter) {
        upCenterPx = convPx(turnMotion.upCenter);
      }
      if (turnMotion.lowCenter) {
        lowCenterPx = convPx(turnMotion.lowCenter);
      }
      if (turnMotion.midCenter) {
        midCenterPx = convPx(turnMotion.midCenter);
      }

      if (upPx.y > -dt.y) {
        clipPx[clipPx.length] = new Point2D(upPx.x, -dt.y);
        backClipPx[backClipPx.length] = new Point2D(upPx.x, -dt.y);
      }
      if (upCenterPx) {
        clipPx[clipPx.length] = upCenterPx;
      } else {
        clipPx[clipPx.length] = upPx;
      }
      if (midCenterPx) {
        clipPx[clipPx.length] = midCenterPx;
      }
      if (lowCenterPx) {
        clipPx[clipPx.length] = lowCenterPx
      } else {
        clipPx[clipPx.length] = lowPx;
      }
      backClipPx[backClipPx.length] = upPx;
      backClipPx[backClipPx.length] = lowPx;
      if (lowPx.y < vsz.y + dt.y) {
        clipPx[clipPx.length] = new Point2D(lowPx.x, vsz.y + dt.y);
        backClipPx[backClipPx.length] = new Point2D(lowPx.x, vsz.y + dt.y);
      }
      if (sideFactor < 0) {
        clipPx[clipPx.length] = new Point2D(vsz.x + dt.x, vsz.y + dt.y);
        backClipPx[backClipPx.length] = new Point2D(-dt.x, vsz.y + dt.y);
      } else {
        clipPx[clipPx.length] = new Point2D(-dt.x, vsz.y + dt.y);
        backClipPx[backClipPx.length] = new Point2D(vsz.x + dt.x, vsz.y + dt.y);
      }

      // clip rotation
      const rotatedClip = []
      for (let px of clipPx) {
        const convPx = vcp.add(px.sub(vcp).rotate(-a))
        rotatedClip.push(convPx)
      }
      const rotatedBackClip = []
      for (let px of backClipPx) {
        const convPx = vcp.add(px.sub(vcp).rotate(-a))
        rotatedBackClip.push(convPx)
      }

      if (clipBackground) {
        // render foreground
        ctx.save();
        ctx.globalAlpha = foreOpacity
        // ctx.strokeStyle = '#00f'
        // ctx.lineWidth = 10
        // ctx.stroke()
        renderItems(view, disposition, session, turnMotion.foregroundPageItems)
        ctx.restore();
        if (turnMotion.backgroundPageItems) {
          // render background
          ctx.save()
          ctx.globalAlpha = backOpacity
          clip(ctx, rotatedBackClip);
          renderItems(view, disposition, session, turnMotion.backgroundPageItems)
          ctx.restore()
        }
      } else {
        if (turnMotion.backgroundPageItems) {
          // render background
          ctx.save()
          ctx.globalAlpha = backOpacity
          renderItems(view, disposition, session, turnMotion.backgroundPageItems)
          ctx.restore()
        }
        // render foreground
        ctx.save();
        ctx.globalAlpha = foreOpacity
        clip(ctx, rotatedClip);
        // ctx.strokeStyle = '#00f'
        // ctx.lineWidth = 10
        // ctx.stroke()
        renderItems(view, disposition, session, turnMotion.foregroundPageItems)
        ctx.restore();
      }

      // // render foreground
      // ctx.save();
      // ctx.globalAlpha = foreOpacity
      // if (!clipBackground) {
      //   clip(ctx, rotatedClip);
      // }
      // // ctx.strokeStyle = '#00f'
      // // ctx.lineWidth = 10
      // // ctx.stroke()
      // renderItems(view, disposition, session, turnMotion.foregroundPageItems)
      // ctx.restore();


      // render shadows
      ctx.save();
      ctx.translate(vcp.x, vcp.y);
      ctx.rotate(a);
      ctx.translate(-vcp.x, -vcp.y);
      ctx.globalAlpha = underOpacity// foreOpacity
      const ftPx = convPx(turnMotion.underSideTop);
      const fbPx = convPx(turnMotion.underSideBottom);
      const upFoldPx = convPx(turnMotion.upFoldPoint);
      const lowFoldPx = convPx(turnMotion.lowFoldPoint);
      const foldDiff = lowFoldPx.sub(upFoldPx);

      const underSideAngle = -turnMotion.underSideAngle
      const underSideDistance = turnMotion.underSideDistance

      let shadowRatio = 0.6;
      let grad = [
        [0, 'rgba(0, 0, 0, ' + (shadowRatio).toFixed(10) + ')'],
        [0.2, 'rgba(0, 0, 0, ' + (0.3 * shadowRatio).toFixed(10) + ')'],
        [0.9,'rgba(0, 0, 0, 0)']
      ];
      const maxShadowLen = z * turnMotion.pageSize.x / 5
      let shadowLen = symProgress * maxShadowLen;
      let shadowAngle = underSideAngle;
      const ang = turnMotion.underSideBorder.angle() + Math.PI / 2
      if (sideFactor < 0) {
        // left side
        fillShadow(ctx, ftPx, fbPx, shadowLen, shadowAngle, -shadowAngle, grad);
      
        radialShadow(ctx, ftPx, shadowLen, shadowAngle - Math.PI / 2, shadowAngle, grad);
        radialShadow(ctx, fbPx, shadowLen, shadowAngle, shadowAngle + Math.PI / 2, grad);

        if (upCenterPx) {
          fillShadow(ctx, ftPx, upCenterPx, shadowLen, shadowAngle - Math.PI / 2, Math.PI / 2 - shadowAngle, grad);
        } else {
          if (turnMotion.upFoldPoint.y <= - turnMotion.pageSize.y / 2) {
            fillShadow(ctx, ftPx, upPx, shadowLen, shadowAngle - Math.PI / 2, Math.PI / 2 - shadowAngle, grad);
          }
        }
        if (lowCenterPx) {
          fillShadow(ctx, fbPx, lowCenterPx, shadowLen, shadowAngle + Math.PI / 2, -Math.PI / 2 - shadowAngle, grad);
        } else {
          if (turnMotion.lowFoldPoint.y >= turnMotion.pageSize.y / 2) {
            fillShadow(ctx, fbPx, lowPx, shadowLen, shadowAngle + Math.PI / 2, -Math.PI / 2 - shadowAngle, grad);
          }
        }
        if (upCenterPx && lowCenterPx) {
          radialShadow(ctx, upCenterPx, shadowLen, ang + Math.PI, shadowAngle - Math.PI / 2, grad);
          radialShadow(ctx, lowCenterPx, shadowLen, shadowAngle + Math.PI / 2, ang + Math.PI, grad);

          fillShadow(ctx, upCenterPx, lowCenterPx, shadowLen, shadowAngle + Math.PI, -shadowAngle + Math.PI, grad);

        } else if (upCenterPx && midCenterPx) {
          radialShadow(ctx, upCenterPx, shadowLen, ang + Math.PI, shadowAngle - Math.PI / 2, grad);
          radialShadow(ctx, midCenterPx, shadowLen, Math.PI, ang + Math.PI, grad);
          radialShadow(ctx, lowFoldPx, shadowLen, shadowAngle + Math.PI / 2, Math.PI, grad);

          fillShadow(ctx, upCenterPx, midCenterPx, shadowLen, shadowAngle + Math.PI, -shadowAngle + Math.PI, grad);

          shadowAngle = Math.PI / 2 - clipAngle;
          shadowLen = symProgress * sideFactor * maxShadowLen;
          fillShadow(ctx, midCenterPx, lowFoldPx, shadowLen, shadowAngle, 0, grad);
        } else if (lowCenterPx && midCenterPx) {
          radialShadow(ctx, upFoldPx, shadowLen, Math.PI, shadowAngle - Math.PI / 2, grad);
          radialShadow(ctx, midCenterPx, shadowLen, ang + Math.PI, Math.PI, grad);
          radialShadow(ctx, lowCenterPx, shadowLen, shadowAngle + Math.PI / 2, ang + Math.PI, grad);

          fillShadow(ctx, midCenterPx, lowCenterPx, shadowLen, shadowAngle + Math.PI, -shadowAngle + Math.PI, grad);

          shadowAngle = Math.PI / 2 - clipAngle;
          shadowLen = symProgress * sideFactor * maxShadowLen;
          fillShadow(ctx, upFoldPx, midCenterPx, shadowLen, shadowAngle, 0, grad);

        } else {
          radialShadow(ctx, upFoldPx, shadowLen, Math.PI, shadowAngle - Math.PI / 2, grad);
          radialShadow(ctx, lowFoldPx, shadowLen, shadowAngle + Math.PI / 2, Math.PI, grad);

          shadowAngle = Math.PI / 2 - clipAngle;
          shadowLen = symProgress * sideFactor * maxShadowLen;
          fillShadow(ctx, upFoldPx, lowFoldPx, shadowLen, shadowAngle, 0, grad);

        }
      } else {
        // right side
        fillShadow(ctx, ftPx, fbPx, shadowLen, shadowAngle + Math.PI, Math.PI - shadowAngle, grad);
        
        radialShadow(ctx, ftPx, shadowLen, Math.PI + shadowAngle, shadowAngle - Math.PI / 2, grad);
        radialShadow(ctx, fbPx, shadowLen, shadowAngle + Math.PI / 2, shadowAngle + Math.PI, grad);

        if (upCenterPx) {
          fillShadow(ctx, ftPx, upCenterPx, shadowLen, shadowAngle - Math.PI / 2, Math.PI / 2 - shadowAngle, grad);
        } else {
          if (turnMotion.upFoldPoint.y <= - turnMotion.pageSize.y / 2) {
            fillShadow(ctx, ftPx, upPx, shadowLen, shadowAngle - Math.PI / 2, Math.PI / 2 - shadowAngle, grad);
          }
        }
        if (lowCenterPx) {
          fillShadow(ctx, fbPx, lowCenterPx, shadowLen, shadowAngle + Math.PI / 2, -Math.PI / 2 - shadowAngle, grad);
        } else {
          if (turnMotion.lowFoldPoint.y >= turnMotion.pageSize.y / 2) {
            fillShadow(ctx, fbPx, lowPx, shadowLen, shadowAngle + Math.PI / 2, -Math.PI / 2 - shadowAngle, grad);
          }
        }
        if (upCenterPx && lowCenterPx) {
          radialShadow(ctx, upCenterPx, shadowLen, shadowAngle - Math.PI / 2, ang, grad);
          radialShadow(ctx, lowCenterPx, shadowLen, ang, shadowAngle + Math.PI / 2, grad);

          fillShadow(ctx, upCenterPx, lowCenterPx, shadowLen, shadowAngle, -shadowAngle, grad);
        } else if (upCenterPx && midCenterPx) {
          radialShadow(ctx, upCenterPx, shadowLen, shadowAngle - Math.PI / 2, ang, grad);
          radialShadow(ctx, midCenterPx, shadowLen, ang, 0, grad);
          radialShadow(ctx, lowFoldPx, shadowLen, 0, shadowAngle + Math.PI / 2, grad);

          fillShadow(ctx, upCenterPx, midCenterPx, shadowLen, shadowAngle, -shadowAngle, grad);

          shadowAngle = Math.PI / 2 - clipAngle;
          shadowLen = symProgress * sideFactor * maxShadowLen;
          fillShadow(ctx, midCenterPx, lowFoldPx, shadowLen, shadowAngle, 0, grad);
        } else if (lowCenterPx && midCenterPx) {
          radialShadow(ctx, upFoldPx, shadowLen, shadowAngle - Math.PI / 2, 0, grad);
          radialShadow(ctx, midCenterPx, shadowLen, 0, ang, grad);
          radialShadow(ctx, lowCenterPx, shadowLen, ang, shadowAngle + Math.PI / 2, grad);

          fillShadow(ctx, midCenterPx, lowCenterPx, shadowLen, shadowAngle, -shadowAngle, grad);

          shadowAngle = Math.PI / 2 - clipAngle;
          shadowLen = symProgress * sideFactor * maxShadowLen;
          fillShadow(ctx, upFoldPx, midCenterPx, shadowLen, shadowAngle, 0, grad);
        } else {
          radialShadow(ctx, upFoldPx, shadowLen, shadowAngle - Math.PI / 2, 0, grad);
          radialShadow(ctx, lowFoldPx, shadowLen, 0, shadowAngle + Math.PI / 2, grad);

          shadowAngle = Math.PI / 2 - clipAngle;
          shadowLen = symProgress * sideFactor * maxShadowLen;
          fillShadow(ctx, upFoldPx, lowFoldPx, shadowLen, shadowAngle, 0, grad);
        }
      }

      ctx.restore();

      // render under side page
      ctx.save();
      ctx.globalAlpha = underOpacity
      clip(ctx, rotatedClip);
      const zsep = turnMotion.centerSep * z;
      const disp = new BoxDisposition();
      disp.zoom = z;
      disp.angle = underSideAngle + a;
      disp.offset.set(disposition.offset.add(underSideDistance.mul(z)).rotate(underSideAngle).add(-sideFactor * zsep / 2, 0))
      const uPage = turnMotion.underSidePage
      let uPageSession;
      if (uPage.scene && uPage.scene.id) {
        uPageSession = session.itemSessions[uPage.scene.id]
      }
      uPage.renderer.render(view, uPage.resource, {
        box: {
          disposition: disp
        }
      }, uPageSession)
      ctx.restore();

      if (!upCenterPx || !lowCenterPx) {
        // render center separated shadow
        ctx.save();
        ctx.globalAlpha = underOpacity
        ctx.translate(vcp.x, vcp.y);
        ctx.rotate(a);
        ctx.translate(-vcp.x, -vcp.y);

        if (upCenterPx && midCenterPx) {
          clip(ctx, [upCenterPx, midCenterPx, lowFoldPx, fbPx, ftPx]);
        } else if (lowCenterPx && midCenterPx) {
          clip(ctx, [upFoldPx, midCenterPx, lowCenterPx, fbPx, ftPx]);
        } else {
          clip(ctx, [upPx, ftPx, fbPx, lowPx]);
        }
        shadowLen = -symProgress * sideFactor * maxShadowLen / 2;
        shadowAngle = Math.PI / 2 - clipAngle;
        shadowRatio = 0.4;

        const centerGrad = [
          [0, 'rgba(0, 0, 0, ' + (shadowRatio).toFixed(10) + ')'],
          [0.2, 'rgba(0, 0, 0, ' + (0.3 * shadowRatio).toFixed(10) + ')'],
          [0.9,'rgba(0, 0, 0, 0)']
        ]
        fillShadow(ctx, upFoldPx.sub(foldDiff), lowFoldPx.add(foldDiff), shadowLen, shadowAngle, 0, centerGrad);

        ctx.restore();
      }

    } else {
      // normal rendering
      for (let i = 0; i < resource.items.length; i++) {
        const item = resource.items[i]
        if (!item) {
          continue
        }
        const itemDisp = item.control.box.disposition

        itemDisp.zoom = z
        itemDisp.angle = a
        itemDisp.offset.x = disposition.offset.x + (item.renderPosition.x * z)
        itemDisp.offset.y = disposition.offset.y + (item.renderPosition.y * z)

        item.contentType.renderer.render(view, item.resource, item.control, session.itemSessions[item.scene.id])
      }
    }

    if (turnMotion && view.session && view.session.turnDragging) {
      // render turning arrow
      const p = view.session.turnDragging.point
      const sz = 20
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(a);

      drawTurnArrow(ctx, {
        size: sz,
        side: turnMotion.side,
        turnSwitchView: view.session.turnSwitchView,
        progress: turnMotion.progress
      })
      ctx.restore();
    }

    return true
  }
}

function drawTurnArrow(ctx, param){
  const sz = param.size
  const side = param.side
  const turnSwitchView = param.turnSwitchView
  const progress = param.progress
  const progressAlpha = Math.min(1, (1 - progress) * 8)
  ctx.globalAlpha = 0.4 * progressAlpha
  ctx.beginPath()
  ctx.arc(0, 0, sz, 0, Math.PI * 2, false)
  ctx.fillStyle = '#555'
  ctx.fill()
  if (turnSwitchView) {
    ctx.globalAlpha = 1 * progressAlpha
  } else {
    ctx.globalAlpha = 0.5 * progressAlpha
  }
  ctx.strokeStyle = '#f00'
  ctx.fillStyle = '#000'

  const deg = Math.PI * 2 / 3

  const sep = 0.55
  const lsz = 0.35
  const sAng = Math.PI - deg
  const sx = sz * Math.cos(sAng) * sep * (1 + lsz)
  const sy = sz * Math.sin(sAng) * sep * (1 + lsz)

  const eAng = Math.PI * 1
  const ex = sz * Math.cos(eAng) * sep
  const ey = sz * Math.sin(eAng) * sep

  ctx.lineWidth = sz * lsz
  ctx.strokeStyle = '#fff'
  ctx.beginPath()
  ctx.moveTo(side * sx, sy)
  ctx.lineTo(side * ex, ey)
  ctx.lineTo(side * sx, -sy)
  ctx.stroke()
}

function calculateTurn(pv, args) {
  const turnOffset = pv.turnOffset
  const sideFactor = pv.sideFactor
  const top = pv.top
  const bottom = pv.bottom
  const turnTop = pv.turnTop
  const turnBottom = pv.turnBottom
  const targetSize = pv.pageSize.add(pv.centerSep, 0)
  const pointOffset = pv.pointOffset
  const turnBase = pv.turnBase
  const point = args.point.sub(pointOffset).sub(turnOffset, 0)
  const centerSep = pv.centerSep

  let ang
  let v1
  let v2
  // let v3

  pv.turning = false
  pv.turningPoint.copy(pv.startPoint)
  // if (top.x > 0) {
  //   if (point.x >= top.x) {
  //     return;
  //   }
  // } else {
  //   if (point.x <= top.x) {
  //     return;
  //   }
  // }

  const normPt = pv.normalizePoint(point, turnBase)

  const vec = turnBase.sub(normPt)

  const foldPoint = turnBase.sub(vec.div(2));
  const foldAngle = Math.PI / 2 - foldPoint.sub(turnBase).angle();
  const foldTan = Math.tan(foldAngle);

  const upClipPoint = pv.upClipPoint
  const lowClipPoint = pv.lowClipPoint
  upClipPoint.x = foldPoint.x - (top.y - foldPoint.y) / foldTan;
  lowClipPoint.x = upClipPoint.x - targetSize.y / foldTan;

  if (pv.sideFactor < 0) {
    if (normPt.x < turnTop.x) {
      upClipPoint.x = turnBase.x
      lowClipPoint.x = turnBase.x
    } else {
      upClipPoint.x = Math.min(0, upClipPoint.x);
      lowClipPoint.x = Math.min(0, lowClipPoint.x);
    }
  } else {
    if (normPt.x > turnTop.x) {
      upClipPoint.x = turnBase.x
      lowClipPoint.x = turnBase.x
    } else {
      upClipPoint.x = Math.max(0, upClipPoint.x);
      lowClipPoint.x = Math.max(0, lowClipPoint.x);
    }
  }

  if (pv.side != pv.sideFactor) {
    if (pv.side < 0) {
      if (upClipPoint.x <= 0 && lowClipPoint.x <= 0) {
        pv.turningPoint.copy(pv.startPoint)
        return;
      }
    } else {
      if (upClipPoint.x >= 0 && lowClipPoint.x >= 0) {
        pv.turningPoint.copy(pv.startPoint)
        return;
      }
    }
  } else {
    if (pv.side < 0) {
      if (upClipPoint.x <= top.x && lowClipPoint.x <= top.x) {
        pv.turningPoint.copy(pv.startPoint)
        return;
      }
    } else {
      if (upClipPoint.x >= top.x && lowClipPoint.x >= top.x) {
        pv.turningPoint.copy(pv.startPoint)
        return;
      }
    }
  }
  pv.turning = true

  const clipDiff = upClipPoint.sub(lowClipPoint);
  pv.clipAngle = -clipDiff.angle();
  const sideClipPoint = new Point2D(turnTop.x, turnTop.y + (turnTop.x - upClipPoint.x) * (-clipDiff.y / -clipDiff.x));
  const upFoldPoint = pv.upFoldPoint
  const lowFoldPoint = pv.lowFoldPoint

  upFoldPoint.set(upClipPoint);
  lowFoldPoint.set(lowClipPoint);
  if (Math.abs(upClipPoint.x) >= targetSize.x) {
    upFoldPoint.set(sideClipPoint);
  } else if (Math.abs(lowClipPoint.x) >= targetSize.x) {
    lowFoldPoint.set(sideClipPoint);
  }

  const underSideTop = pv.underSideTop
  const underSideBottom = pv.underSideBottom
  underSideTop.set(sideClipPoint);
  underSideBottom.set(sideClipPoint);
  const underSideAngle = 2 * (pv.clipAngle - Math.PI / 2);
  if (Math.abs(upClipPoint.x) < Math.abs(turnBase.x)) {
    underSideTop.set(upFoldPoint.sub(turnTop.sub(upFoldPoint).rotate(underSideAngle)));
  }
  if (Math.abs(lowClipPoint.x) < Math.abs(turnBase.x)) {
    underSideBottom.set(lowFoldPoint.sub(turnBottom.sub(lowFoldPoint).rotate(underSideAngle)));
  }

  pv.underSideBorder = underSideTop.sub(underSideBottom);
  const ft = upClipPoint.sub((turnTop).sub(upClipPoint).rotate(underSideAngle));
  const fb = lowClipPoint.sub((turnBottom).sub(lowClipPoint).rotate(underSideAngle));

  const turnPt = new Point2D()

  turnPt.set(ft.add(new Point2D(0, Math.max(0, Math.min(targetSize.y, turnBase.y - turnTop.y))).rotate(underSideAngle)))
  pv.turningPoint.copy(turnPt.add(pointOffset).add(turnOffset, 0))

  let upCenter = null;
  let midCenter = null;
  let lowCenter = null;
  let upCentDiff = upFoldPoint.x - sideFactor * centerSep;
  let lowCentDiff = lowFoldPoint.x - sideFactor * centerSep;
  let upSep = -sideFactor * upCentDiff > 0;
  let lowSep = -sideFactor * lowCentDiff > 0;

  if (upSep && lowSep) {
    ang = pv.underSideBorder.angle() + Math.PI / 2;
    upCenter = ft.add( (new Point2D((turnTop.x - sideFactor * centerSep), 0)).rotate(-ang) );
    lowCenter = upCenter.sub(new Point2D(0, -pv.pageSize.y).rotate(-ang));

  } else if (upSep) {
    ang = pv.underSideBorder.angle() + Math.PI / 2;
    upCenter = ft.add( (new Point2D((turnTop.x - sideFactor * centerSep), 0)).rotate(-ang) );
    v2 = sideFactor * centerSep - upCenter.x;
    v1 = Math.min(0, Math.max(-pv.pageSize.y, -v2 / Math.sin(-ang)));
    midCenter = upCenter.sub(new Point2D(0, v1).rotate(-ang));

  } else if (lowSep) {
    ang = pv.underSideBorder.angle() + Math.PI / 2;
    lowCenter = fb.add( (new Point2D((turnTop.x - sideFactor * centerSep), 0)).rotate(-ang) );
    v2 = sideFactor * centerSep - lowCenter.x
    v1 = Math.min(pv.pageSize.y, Math.max(0, -v2 / Math.sin(-ang)));
    midCenter = lowCenter.add(new Point2D(0, -v1).rotate(-ang));
  }

  pv.upCenter = upCenter
  pv.midCenter = midCenter
  pv.lowCenter = lowCenter

  // TODO 面積で計算？
  pv.progress = Math.min(1, Math.max(0, ((upFoldPoint.x + lowFoldPoint.x) / 2 / turnTop.x)));
  if (!pv.progressReversed) {
    pv.progress = 1 - pv.progress
  }

  // v1 = turnBase.sub(upFoldPoint);
  // v2 = -v1.angle();
  // v3 = -lowFoldPoint.sub(upFoldPoint).angle();

  pv.underSideDistance = ft.add(fb).div(2).add(new Point2D(turnTop.x / 2, 0).rotate(underSideAngle));

  let diff = 0;
  upClipPoint.x += turnOffset + diff;
  lowClipPoint.x += turnOffset + diff;
  upFoldPoint.x += turnOffset + diff;
  lowFoldPoint.x += turnOffset + diff;
  underSideTop.x += turnOffset + diff;
  underSideBottom.x += turnOffset + diff;
  pv.underSideDistance.x += turnOffset + diff;
  pv.underSideAngle = underSideAngle
}

const turnStartPixel = 50
class TurnMotion {
  constructor(options){

    const pv = privateMap(this);
    // pv.pageSize = options.pageSize
    const centerSep = options.centerSep ? options.centerSep : 0
    pv.size = options.size
    pv.side = options.side
    pv.sideFactor = options.sideFactor ? options.sideFactor : pv.side
    pv.fromView = options.fromView
    pv.toView = options.toView
    pv.basePoint = options.basePoint
    pv.centerSep = 0
    pv.turnOffset = 0

    if (pv.fromView.items.length == 1) {
      // single turn
      const item = pv.fromView.items[0]
      pv.pageSize = new Point2D(item.resource.size)
      if (pv.side != pv.sideFactor) {
        pv.progressReversed = true
        pv.foregroundPageItems = []
        pv.backgroundPageItems = pv.toView.items
        pv.underSidePage = {
          scene: item.scene,
          resource: item.resource,
          renderer: item.contentType.renderer
        }
      } else {
        pv.foregroundPageItems = pv.fromView.items
        pv.backgroundPageItems = pv.toView.items
      }
      if (pv.side < 0) {
        pv.turnOffset = item.resource.size.x / 2
      } else {
        pv.turnOffset = -item.resource.size.x / 2
      }
    } else if (pv.fromView.items.length == 2) {
      // paged turn
      let background
      let idx1 = 0
      let idx2 = 1
      if (pv.side != pv.sideFactor) {
        pv.progressReversed = true
        pv.foregroundPageItems = pv.toView.items
        background = pv.fromView.items
        idx1 = 1
        idx2 = 0
      } else {
        pv.foregroundPageItems = pv.fromView.items
        background = pv.toView.items
      }
      pv.turnOffset = 0
      if (pv.side < 0) {
        pv.pageSize = new Point2D(pv.fromView.items[0].resource.size)
        pv.centerSep = centerSep
        if (background.length == 2) {
          const underSideItem = background[idx2]
          if (underSideItem) {
            pv.underSidePage = {
              scene: underSideItem.scene,
              resource: underSideItem.resource,
              renderer: underSideItem.contentType.renderer
            }
            pv.backgroundPageItems = [background[idx1]]
          }
        } else {
          pv.backgroundPageItems = background
        }

      } else {
        pv.pageSize = new Point2D(pv.fromView.items[1].resource.size)
        pv.centerSep = centerSep
        pv.turnOffset = 0
        if (background.length == 2) {
          const underSideItem = background[idx1]
          if (underSideItem) {
            pv.underSidePage = {
              scene: underSideItem.scene,
              resource: underSideItem.resource,
              renderer: underSideItem.contentType.renderer
            }
            pv.backgroundPageItems = [background[idx2]]
          }
        } else {
          pv.backgroundPageItems = background
        }
      }
    }
    if (!pv.underSidePage) {
      pv.underSidePage = {
        scene: null,
        resource: {
          backgroundColor: '#eee',
          size: pv.pageSize
        },
        renderer: emptyPageRenderer
      }
    }

    pv.top = new Point2D()
    pv.bottom = new Point2D()
    pv.startPoint = new Point2D()
    pv.turningPoint = new Point2D()
    pv.upClipPoint = new Point2D()
    pv.lowClipPoint = new Point2D()
    pv.upFoldPoint = new Point2D()
    pv.lowFoldPoint = new Point2D()
    pv.clipAngle = 0
    pv.underSideTop = new Point2D()
    pv.underSideBottom = new Point2D()
    pv.underSideAngle = 0
    pv.underSideBorder = null
    pv.underSideDistance = null
    pv.progress = 0
    pv.turning = false

    pv.normalizePoint = function(pt, turnBase){
      const vec = turnBase.sub(pt)
      const d = vec.distance()
      const limitY = Math.abs(vec.x) / 3 * Math.min(1, d / Math.abs(pv.top.x / 3) )
      let normPt = pt
      if (Math.abs(vec.y) > limitY) {
        normPt = new Point2D(pt.x, turnBase.y + (vec.y < 0 ? limitY : -limitY));
      }
      return normPt;
    }
    const halfHeight = pv.pageSize.y / 2
    const x = pv.pageSize.x + pv.centerSep
    if (pv.side < 0) {
      pv.top.set(-x, -halfHeight)
      pv.bottom.set(-x, halfHeight)
      pv.endX = (pv.size.x / 2) + x + pv.turnOffset
    } else {
      pv.top.set(x, -halfHeight)
      pv.bottom.set(x, halfHeight)
      pv.endX = (pv.size.x / 2) - x + pv.turnOffset
    }
    pv.upClipPoint.y = pv.top.y;
    pv.lowClipPoint.y = pv.bottom.y;
    pv.pointOffset = pv.size.div(2)
    if (!pv.basePoint) {
      pv.basePoint = new Point2D(pv.top.x + pv.pointOffset.x + pv.turnOffset, pv.size.y / 2)
    }
    pv.startPoint.set(pv.top.x + pv.pointOffset.x + pv.turnOffset, Math.min(Math.max(0, pv.basePoint.y), pv.pageSize.y))
    pv.turnBase = new Point2D(pv.top.x, pv.basePoint.y - pv.pointOffset.y)

    if (pv.sideFactor != pv.side) {
      pv.turnBase.x = -pv.turnBase.x
      pv.turnTop = new Point2D(-pv.top.x, pv.top.y)
      pv.turnBottom = new Point2D(-pv.bottom.x, pv.bottom.y)
    } else {
      pv.turnTop = new Point2D(pv.top.x, pv.top.y)
      pv.turnBottom = new Point2D(pv.bottom.x, pv.bottom.y)
  }

  }
  get startPoint(){
    return privateMap(this).startPoint
  }
  get turningPoint(){
    return privateMap(this).turningPoint
  }
  get centerSep(){
    return privateMap(this).centerSep
  }
  get endX(){
    return privateMap(this).endX
  }

  get fromView(){
    return privateMap(this).fromView
  }
  get toView(){
    return privateMap(this).toView
  }

  get backgroundPageItems(){
    return privateMap(this).backgroundPageItems
  }
  get foregroundPageItems(){
    return privateMap(this).foregroundPageItems
  }
  get side(){
    return privateMap(this).side
  }
  get size(){
    return privateMap(this).size
  }
  get sideFactor(){
    return privateMap(this).sideFactor
  }
  get pageSize(){
    return privateMap(this).pageSize
  }

  get turning(){
    return privateMap(this).turning
  }
  get progress(){
    return privateMap(this).progress
  }
  get progressReversed(){
    return privateMap(this).progressReversed
  }

  get clipAngle(){
    return privateMap(this).clipAngle
  }
  get upClipPoint(){
    return privateMap(this).upClipPoint
  }
  get lowClipPoint(){
    return privateMap(this).lowClipPoint
  }
  get underSideTop(){
    return privateMap(this).underSideTop
  }
  get upFoldPoint(){
    return privateMap(this).upFoldPoint
  }
  get lowFoldPoint(){
    return privateMap(this).lowFoldPoint
  }
  get underSideBottom(){
    return privateMap(this).underSideBottom
  }
  get underSideBorder(){
    return privateMap(this).underSideBorder
  }
  get underSideDistance(){
    return privateMap(this).underSideDistance
  }
  get underSideAngle(){
    return privateMap(this).underSideAngle
  }

  get underSidePage(){
    return privateMap(this).underSidePage
  }

  get upCenter(){
    return privateMap(this).upCenter
  }
  get lowCenter(){
    return privateMap(this).lowCenter
  }
  get midCenter(){
    return privateMap(this).midCenter
  }

  turn(args){
    const pv = privateMap(this);
    calculateTurn(pv, args)
  }
}

const emptyPageRenderer = {
  render: function(view, resource, control, session){
    const vc = view.center
    const rotateCenter = vc;
    const boxControl = control.box
    const sz = resource.size.mul(boxControl.disposition.zoom)
    const angle = boxControl.disposition.angle
    const ofs = boxControl.disposition.offset.sub(sz.div(2));
    const ctx = view.context2d

    ctx.save()

    ctx.translate(rotateCenter.x, rotateCenter.y);
    ctx.rotate(angle);
    ctx.translate(-rotateCenter.x, -rotateCenter.y);

    const backgroundColor = resource.backgroundColor ? resource.backgroundColor : '#eee';
    ctx.fillStyle = backgroundColor;
    // ctx.fillRect(ofs.x + vc.x + 0.5, ofs.y + vc.y + 0.5, sz.x - 1, sz.y - 1);
    ctx.fillRect(ofs.x + vc.x, ofs.y + vc.y, sz.x, sz.y);

    ctx.restore()
  }
}


export class PagedSequenceView {
  constructor(options){
    const pv = privateMap(this);
    pv.resourceLoaders = options.resourceLoaders;
    pv.annotationListResourceLoader = options.annotationListResourceLoader;
    pv.contentControls = options.contentControls;
    pv.contentViewComponents = options.contentViewComponents ? options.contentViewComponents : [];
    pv.sceneComponents = options.sceneComponents ? options.sceneComponents : [];
    pv.contentTypes = options.contentTypes;
    pv.createErrorMessage = options.createErrorMessage;
    pv.createUnknownTypeScene = options.createUnknownTypeScene;
    pv.openDomLayer = options.openDomLayer;
    pv.openMessageLayer = options.openMessageLayer;
    pv.closeDomLayer = options.closeDomLayer;
    pv.closeMessageLayer = options.closeMessageLayer;
    pv.loadingIndicator = options.loadingIndicator;
    pv.perPixelTurnAnimationFrame = options.perPixelTurnAnimationFrame ? options.perPixelTurnAnimationFrame : 0.8;
    pv.maxTurnAnimationDuration = options.maxTurnAnimationDuration ? options.maxTurnAnimationDuration : 200;
    pv.defaultPageIntervalWidth = options.defaultPageIntervalWidth ? options.defaultPageIntervalWidth : 0.1;
    pv.calcSideFactor = options.calcSideFactor ? options.calcSideFactor : (controller, side) => controller.reverseOrder ? -1 : 1;
    // pv.calcSideFactor = options.calcSideFactor ? options.calcSideFactor : (controller, side) => side;

    pv.currentContentViewComponents = [];
    pv.resourceLoadObj = false;

    pv.eventLayer = domUtil.createElement('div')
    domUtil.asAbsoluteFit(pv.eventLayer)
    pv.createTurnAnimation = function(turnMotion, endPt, algorhithm){
      const contentView = pv.contentView
      const boxControl = contentView.control.box
      const start = (new Date()).getTime()
      const startPt = new Point2D()

      startPt.copy(turnMotion.turningPoint)
      const deltaPt = endPt.sub(startPt)
      const dur = Math.min(pv.maxTurnAnimationDuration, deltaPt.distance() * boxControl.disposition.zoom * pv.perPixelTurnAnimationFrame)

      let animationTimeout
      const turnAnimationAlgorhithm = algorhithm ? algorhithm : {
        calculate: (pt) => {
          const dr = (pt.x - startPt.x) / (endPt.x - startPt.x)
          if (dr < 0) {
            let endX
            if (endPt.x == turnMotion.startPoint.x) {
              endX = turnMotion.endX
            } else {
              endX = turnMotion.startPoint.x
            }
            if (endX == startPt.x) {
              pt.y = startPt.y
            } else {
              pt.y = startPt.y - (startPt.y - endPt.y) * (1 - (pt.x - endX) / (startPt.x - endX))
            }
          } else {
            pt.y = startPt.y + dr * deltaPt.y
          }
          return pt
        }
      }
      const animation = {
        algorhithm: turnAnimationAlgorhithm,
        animate: function(){
          if (!pv.contentView) {
            return
          }
          const boxControl = pv.contentView.control.box
          const now = (new Date()).getTime()
          const dt = now - start
          animationTimeout = null
          if (dt > dur) {
            this.stop()
            return
          }
          const dr = dt / dur
          const pt = startPt.add(deltaPt.mul(dr))
          const turnPoint = turnAnimationAlgorhithm.calculate(pt)
          turnMotion.turn({
            boxControl: boxControl,
            point: turnPoint
          })
          // pv.contentView.session.debug = {
          //   shapes: [
          //     {style: {strokeStyle:'#f00'}, type:'point', px:boxControl.getViewPixel(turnPoint)}
          //   ]
          // }
          pv.contentView.requestRender()
          animationTimeout = requestAnimationFrame(() => {
            this.animate()
          })
        },
        stop: function(){
          pv.animation = null
          if (animationTimeout) {
            cancelAnimationFrame(animationTimeout)
          }
          if(pv.contentView) {
            pv.contentView.annotationDisabled = false
            if (pv.contentView.session) {
              pv.contentView.session.turnMotion = null
            }
            pv.contentView.requestRender()
          }
        }
      }
      return animation
    }
    pv.createTurnMotion = function(fromViewNo, toViewInc, resource, side, options){
      const turnMotionOptions = {
        fromView: {...pv.controller.getView(fromViewNo), items: resource.items},
        toView: {...pv.controller.getView(fromViewNo + toViewInc)},
        size: new Point2D(resource.size),
        foregroundPageItems: resource.items,
        side: side,
        basePoint: options.basePoint,
        sideFactor: pv.calcSideFactor(pv.controller, side),
        centerSep: pv.turnCenterSep / 2
      }
      // const centerSep = pv.turnCenterSep / 2

      if (side < 0) {
          // left turn
        turnMotionOptions.toView.items = resource.left.items
      } else {
        // right turn
        turnMotionOptions.toView.items = resource.right.items
      }
      return new TurnMotion(turnMotionOptions)
    }
    const dragStart = (ev, touch) => {
      if (!pv.controller) {
        return
      }
      const view = pv.contentView
      if (!view) {
        return
      }
      view.session.turnMotion = null
      const boxControl = view.control.box
      if (!boxControl) {
        return
      }
      const resource = view.resource;
      if (!resource) {
        return
      }
      if (pv.animation) {
        pv.animation.stop()
      }

      // const touch = domUtil.generateTouch(ev)
      const viewOfs = domUtil.getOffsetPosition(view.domEl)
      const viewPx = touch.center.sub(viewOfs)
      const boxPt = boxControl.getBoxPoint(viewPx)
      const turn =  turnStartPixel / boxControl.disposition.zoom
      let turnSide;

      const fromViewNo = pv.controller.viewNo
      if (resource.left && boxPt.x < resource.leftEdge + turn) {
        // left turn
        turnSide = -1
      } else if (resource.right && boxPt.x > resource.rightEdge - turn){
        // right turn
        turnSide = 1
      } else {
        return
      }
      ev.preventDefault()
      ev.stopPropagation()
      const toViewInc = pv.controller.reverseOrder ? -turnSide : turnSide
      const turnMotion = pv.createTurnMotion(fromViewNo, toViewInc, resource, turnSide, {
        basePoint: boxPt
      })
      view.session.turnMotion = turnMotion
      view.session.turnDragging = {
        viewPx: new Point2D(viewPx),
        point: new Point2D()
      }
      view.session.turnDragging.point.copy(boxControl.getViewPixel(turnMotion.startPoint))
      view.requestRender()
    }
    const dragMove = (ev, touch) => {
      const view = pv.contentView
      if (!view) {
        return
      }
      const boxControl = view.control.box
      if (!boxControl) {
        return
      }
      const resource = view.resource;
      if (!resource) {
        return
      }
      const turnMotion = view.session.turnMotion
      if (!turnMotion) {
        return
      }
      // const touch = domUtil.generateTouch(ev)
      const viewOfs = domUtil.getOffsetPosition(view.domEl)
      const viewPx = touch.center.sub(viewOfs)
      const boxPt = boxControl.getBoxPoint(viewPx)
      view.annotationDisabled = true
      turnMotion.turn({
        point: boxPt
      })
      const dPt = boxPt.sub(turnMotion.startPoint)
      const dPx = dPt.mul(boxControl.disposition.zoom);
      const switchPx = Math.min(Math.min(view.size.x, view.size.y) / 2, boxControl.disposition.zoom * turnMotion.pageSize.x / 2 )
      let switchView = turnMotion.side < 0 ? dPx.x > switchPx : dPx.x < -switchPx
      view.session.turnSwitchView = switchView

      view.session.turnDragging.point.copy(boxControl.getViewPixel(turnMotion.turningPoint))
      view.session.turnDragging.viewPx.copy(viewPx)

      view.requestRender()
    }
    const dragEnd = (ev) => {
      const view = pv.contentView
      if (!view) {
        return
      }
      const turnMotion = view.session.turnMotion
      if (!turnMotion) {
        return
      }
      view.session.turnDragging = false
      view.annotationDisabled = false
      if (view.session.turnSwitchView) {
        // switch
        if (pv.controller) {
          pv.handOverContentViewSession = pv.contentView.session
          pv.handOverViewItems = turnMotion.toView.items
          pv.handOverAnimation = pv.createTurnAnimation(turnMotion, new Point2D(turnMotion.endX, turnMotion.startPoint.y))
          pv.controller.viewNo = turnMotion.toView.viewNo
        }
      } else {
        // rewind
        if (pv.animation) {
          pv.animation.stop()
        }
        if (!turnMotion.turning) {
          pv.animation = null
          view.session.turnMotion = null
          view.requestRender()
          return
        }
        view.annotationDisabled = true
        pv.animation = pv.createTurnAnimation(turnMotion, new Point2D(turnMotion.startPoint))
        pv.animation.animate()
        view.requestRender()
      }

    }
    pv.eventLayer.addEventListener('mousedown', function(ev){
      dragStart(ev, domUtil.generateTouch(ev))
      const moveHandler = (ev) => {
        dragMove(ev, domUtil.generateTouch(ev))
      };
      domUtil.addEventListener(window, 'mousemove', moveHandler);
      const endHandler = (ev) => {
        domUtil.removeEventListener(window, 'mousemove', moveHandler);
        domUtil.removeEventListener(window, 'mouseup', endHandler);
        dragEnd(ev)
      };
      domUtil.addEventListener(window, 'mouseup', endHandler);
      domUtil.addEventListener(window, 'blur', endHandler);
    })
    pv.eventLayer.addEventListener('touchstart', (ev) => {
      if (pv.touching) {
        return;
      }
      pv.touching = true;
      dragStart(ev, domUtil.getTouch(ev))
      const moveHandler = (ev) => {
        dragMove(ev, domUtil.getTouch(ev))
      };
      domUtil.addEventListener(window, 'touchmove', moveHandler);

      const endHandler = function(ev){
        const touch = domUtil.getTouch(ev);
        if (touch.points.length > 0) {
          return;
        }
        domUtil.removeEventListener(window, 'touchmove', moveHandler);
        domUtil.removeEventListener(window, 'touchend', endHandler);
        pv.touching = false;
        dragEnd(ev)
      };
      domUtil.addEventListener(window, 'touchend', endHandler);
      domUtil.addEventListener(window, 'blur', endHandler);
    })

    pv.viewchangeListener = (ev) => {
      resetSequenceViewScene(pv);
      changeSequenceViewScene(pv);
    };
    function doTurn(fromViewNo, toViewInc, turnSide){
      const view = pv.contentView
      if (!view) {
        return
      }
      let prevTurnMotion
      if (view.session.turnMotion) {
        prevTurnMotion = view.session.turnMotion
      }
      let prevAlgorhithm
      if (pv.animation) {
        prevAlgorhithm = pv.animation.algorhithm
        pv.animation.stop()
        pv.animation = null
      }
      const boxControl = view.control.box
      if (!boxControl) {
        return
      }
      const resource = view.resource;
      if (!resource) {
        return
      }
      pv.handOverContentViewSession = view.session

      const toViewNo = fromViewNo + toViewInc
      let curFromView
      let curToView
      if (prevTurnMotion) {
        curFromView = prevTurnMotion.fromView
        curToView = prevTurnMotion.toView
      }
      let turnMotion
      let endPt
      let algorhithm

      if (curFromView && curToView && ((curFromView.viewNo == toViewNo) || (curToView.viewNo == toViewNo))) {
        // use current turn motion
        turnMotion = prevTurnMotion
        pv.handOverContentViewSession.turnMotion = turnMotion
        if (curFromView.viewNo == toViewNo) {
          // cancel turn
          endPt = new Point2D(turnMotion.startPoint)
          pv.handOverViewItems = turnMotion.fromView.items
        } else {
          // turn
          endPt = new Point2D(turnMotion.endX, turnMotion.startPoint.y)
          pv.handOverViewItems = turnMotion.toView.items
        }
        algorhithm = prevAlgorhithm
      } else {
        turnMotion = pv.createTurnMotion(fromViewNo, toViewInc, resource, turnSide, {})
        turnMotion.turn({
          point: turnMotion.startPoint
        })
        endPt = new Point2D(turnMotion.endX, turnMotion.startPoint.y)
        pv.handOverViewItems = turnMotion.toView.items

        pv.handOverContentViewSession.turnMotion = turnMotion
        const pageWidth = turnMotion.pageSize.x
        const yLimit = pageWidth / 5
        algorhithm = {
          calculate: function(pt){
            const dr = (pt.x - turnMotion.startPoint.x) / (turnMotion.endX - turnMotion.startPoint.x)
            if (dr < 0.5) {
              pt.y = turnMotion.startPoint.y - yLimit * (dr * 2)
            } else {
              pt.y = turnMotion.startPoint.y - yLimit * ((1 - dr) * 2)
            }
            return pt
          }
        }
      }

      pv.handOverAnimation = pv.createTurnAnimation(turnMotion, endPt, algorhithm)
    }
    pv.nextviewListener = (ev) => {
      if (!pv.controller) {
        return
      }
      doTurn(pv.controller.viewNo, 1, pv.controller.reverseOrder ? -1 : 1)
    }
    pv.prevviewListener = (ev) => {
      if (!pv.controller) {
        return
      }
      doTurn(pv.controller.viewNo, -1, pv.controller.reverseOrder ? 1 : -1)
    }
  }

  set controller(controller) {
    const pv = privateMap(this);
    pv.session = {}
    if (pv.controller) {
      pv.controller.removeEventListener('viewchange', pv.viewchangeListener);
      pv.controller.removeEventListener('nextview', pv.nextviewListener);
      pv.controller.removeEventListener('prevview', pv.prevviewListener);
    }
    resetSequenceViewScene(pv);
    const views = []
    pv.controller = controller;
    pv.handOverAnimation = null;
    pv.handOverContentViewSession = null;
    pv.handOverViewItems = null;
    if (pv.animation) {
      pv.animation.stop()
      pv.animation = null
    }
    if (pv.controller) {
      let pagedSceneIdx = null
      let pagedScene = null
      let sceneIdx = 0
      let reversed = controller.reverseOrder
      let maxPageWidth = 0
      // let reversed = true
      pv.controller.eachScene((scene) => {
        if (scene.type != 'iiifcanvas') {
          return
        }
        if (scene.viewingHint == 'non-paged') {
          if (pagedScene != null) {
            views.push([pagedSceneIdx])
            pagedSceneIdx = null
            pagedScene = null
          }
          views.push([sceneIdx])
        } else {
          maxPageWidth = scene.source.width
          if (pagedScene != null) {
            if (reversed) {
              views.push([sceneIdx, pagedSceneIdx])
            } else {
              views.push([pagedSceneIdx, sceneIdx])
            }
            pagedSceneIdx = null
            pagedScene = null
          } else if (views.length == 0) {
            if (reversed) {
              views.push([sceneIdx, null])
            } else {
              views.push([null, sceneIdx])
            }
          } else {
            pagedSceneIdx = sceneIdx
            pagedScene = scene
          }
        }
        sceneIdx++
      })

      if (pagedScene) {
        if (views.length > 0 && views[views.length - 1] && views[views.length - 1].length == 2) {
          if (reversed) {
            views.push([null, pagedSceneIdx])
          } else {
            views.push([pagedSceneIdx, null])
          }
        } else {
          views.push([pagedSceneIdx])
        }
      }
      let pageIntervalWidth = pv.defaultPageIntervalWidth
      if (controller.sequenceOptions && controller.sequenceOptions.pageIntervalWidth) {
        pageIntervalWidth = controller.sequenceOptions.pageIntervalWidth
      }
      pv.turnCenterSep = maxPageWidth * pageIntervalWidth

      const seqViews = []
      let firstIdx = 0;
      let secondIdx = 1;
      if (reversed) {
        firstIdx = 1;
        secondIdx = 0;
      }
      let prevSeqView
      for (let viewIdx = 0; viewIdx < views.length; viewIdx++) {
        const view = views[viewIdx]
        const seqView = {
          viewNo: viewIdx,
          sceneIdxs: view,
          sceneParams:[],
          size: new Point2D()
        }
        if (view.length == 2) {
          // paged
          let firstPage
          if (view[firstIdx] !== null) {
            const scene = pv.controller.getScene(view[firstIdx])
            const pageSize = new Point2D(scene.source.width, scene.source.height)
            firstPage = {
              size: pageSize
            }
            // first page
            if (prevSeqView) {
              // prev view
              const prevParams = prevSeqView.sceneParams
              if (prevParams.length == 2) {
                const pairParam = prevParams[secondIdx]
                if (pairParam) {
                  pageSize.x = Math.max(pageSize.x, pairParam.size.x)
                  pageSize.y = Math.max(pageSize.y, pairParam.size.y)
                  pairParam.size.copy(pageSize)
                }
              }
            }
            // maxPageSize.x = Math.max(maxPageSize.x, pageSize.x)
            // maxPageSize.y = Math.max(maxPageSize.y, pageSize.y)
          }
          seqView.sceneParams[firstIdx] = firstPage

          let secondPage
          if (view[secondIdx] !== null) {
            const scene = pv.controller.getScene(view[secondIdx])
            const pageSize = new Point2D(scene.source.width, scene.source.height)
            secondPage = {
              size: pageSize
            }
            // maxPageSize.x = Math.max(maxPageSize.x, pageSize.x)
            // maxPageSize.y = Math.max(maxPageSize.y, pageSize.y)
          }
          seqView.sceneParams[secondIdx] = secondPage
          // seqView.size.set(maxPageSize.x * 2, maxPageSize.y)
        } else {
          // non paged
          const scene = pv.controller.getScene(view[0])
          seqView.size.set(scene.source.width, scene.source.height)
        }
        seqViews.push(seqView)
        prevSeqView = seqView
      }
      // calculate view's width and height
      const maxSize = new Point2D()
      for (let seqView of seqViews) {
        if (seqView.sceneParams && seqView.sceneParams.length == 2) {
          const maxPageSize = new Point2D()
          if (seqView.sceneParams[0]) {
            maxPageSize.x = Math.max(maxPageSize.x, seqView.sceneParams[0].size.x)
            maxPageSize.y = Math.max(maxPageSize.y, seqView.sceneParams[0].size.y)
          }
          if (seqView.sceneParams[1]) {
            maxPageSize.x = Math.max(maxPageSize.x, seqView.sceneParams[1].size.x)
            maxPageSize.y = Math.max(maxPageSize.y, seqView.sceneParams[1].size.y)
          }
          seqView.size.set(maxPageSize.x * 2 + pv.turnCenterSep, maxPageSize.y)
        }
        maxSize.x = Math.max(maxSize.x, seqView.size.x)
        maxSize.y = Math.max(maxSize.y, seqView.size.y)
      }

      pv.controller.addEventListener('viewchange', pv.viewchangeListener);
      pv.controller.addEventListener('nextview', pv.nextviewListener);
      pv.controller.addEventListener('prevview', pv.prevviewListener);
      // pv.controller.views = views
      pv.controller.views = seqViews
      pv.maxSize = maxSize
      if (pv.controller.sceneNos.length > 0) {
        changeSequenceViewScene.call(this, pv);
      } else {
        pv.contentView.reset();
      }
    }
  }

  link(contentView){
    const pv = privateMap(this);
    pv.contentView = contentView
    if (pv.contentView) {
      pv.contentView.reset();
      pv.contentView.eventLayer.appendChild(pv.eventLayer)
    }
  }
  unlink(){
    const pv = privateMap(this);
    if (pv.animation) {
      pv.animation.stop()
    }
    pv.animation = null
    resetSequenceViewScene(pv)
    if (pv.contentView) {
      for (let event in pv.eventListeners) {
        pv.contentView.eventLayer.removeEventListener(event, pv.eventListeners[event])
      }
      pv.contentView.reset();
    }
    pv.contentView = null
    if (pv.eventLayer.parentNode) {
      pv.eventLayer.parentNode.removeChild(pv.eventLayer)
    }
  }

  // get sceneNo(){
  //   privateMap(this).currentSceneNo;
  // }
  // set sceneNo(no){
  //   const pv = privateMap(this);
  //   if (!pv.controller) {
  //     return;
  //   }
  //   const scenes = pv.sequence.scenes;
  //   const contentView = pv.contentView;
  //   const sceneNo = Math.min(scenes.length, Math.max(0, no));
  // }
}